/**
 * Monthlees referral surfaces — the invite banner (theme-wide), /pages/refer,
 * and the referee-page name lookup on /pages/invited.
 *
 * App Proxy endpoints (confirmed, not invented — see docs/design-handoff/00-START-HERE.md):
 *   GET  /apps/precision/referral                       {ok, code, url, rewardUsd, joined, earned, pending, pendingReferrals:[{orderId, createdAt, nudgedAt}]}
 *   POST /apps/precision/referral?action=nudge  {orderId}   -> {ok, nudged:true} | 404 | 409 {code}
 *   GET  /apps/precision/referral-who?r=CODE             {ok, firstName}  — firstName null is the unnamed state
 *
 * Reward is always store credit (rewardUsd, typically 15.79). There is no
 * monthly-vs-annual / skipped-payment branch anymore.
 *
 * The referral code lives in the query string as `r`, never `ref` (GoAffPro owns
 * that key). The nudge button sends an orderId only — no free-text invite field,
 * ever: a member can only nudge a pending referral already on their own list.
 */
(function () {
  'use strict';

  var BANNER_DISMISS_KEY = 'monthlees_refer_banner_dismissed_v1';

  function qs(root, sel) {
    return root ? root.querySelector(sel) : null;
  }

  function qsa(root, sel) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(sel)) : [];
  }

  function getReferralCodeFromUrl() {
    try {
      return new URLSearchParams(window.location.search || '').get('r') || '';
    } catch (e) {
      return '';
    }
  }

  function getReferralCodeFromAttribution() {
    try {
      if (window.MonthleesAttribution && typeof window.MonthleesAttribution.get === 'function') {
        var rec = window.MonthleesAttribution.get();
        return (rec && rec.r) || '';
      }
    } catch (e) {
      /* ignore */
    }
    return '';
  }

  function isActiveMemberBody() {
    try {
      return document.body && document.body.getAttribute('data-monthlees-customer-member') === 'active';
    } catch (e) {
      return false;
    }
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (res) {
      return res
        .text()
        .then(function (text) {
          var data = {};
          if (text) {
            try {
              data = JSON.parse(text);
            } catch (e) {
              data = {};
            }
          }
          return { ok: res.ok, status: res.status, data: data };
        });
    });
  }

  function getReferralWho(code) {
    return fetchJson('/apps/precision/referral-who?r=' + encodeURIComponent(code)).then(function (result) {
      return result.data || {};
    });
  }

  /* ---------------- Invite banner (self-gated, rendered once in theme.liquid) ---------------- */

  function initBanner() {
    var banner = document.querySelector('[data-m-refer-banner]');
    if (!banner) return;

    if (isActiveMemberBody()) return; // already a member — cannot be referred, suppress entirely
    try {
      if (window.localStorage.getItem(BANNER_DISMISS_KEY) === '1') return;
    } catch (e) {
      /* ignore, treat as not dismissed */
    }

    var code = banner.getAttribute('data-m-refer-banner-r') || getReferralCodeFromUrl() || getReferralCodeFromAttribution();
    if (!code) return;

    var whoEl = qs(banner, '[data-m-refer-banner-who]');
    var dismissBtn = qs(banner, '[data-m-refer-banner-dismiss]');

    function show() {
      banner.classList.add('is-visible');
    }

    getReferralWho(code)
      .then(function (data) {
        if (data && data.ok && data.firstName && whoEl) {
          whoEl.textContent = data.firstName + ' invited you. ';
        }
        show();
      })
      .catch(function () {
        // Name lookup failing must not hide the banner — the unnamed copy already in
        // the markup ("A friend invited you") is the designed fallback state.
        show();
      });

    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        banner.classList.remove('is-visible');
        try {
          window.localStorage.setItem(BANNER_DISMISS_KEY, '1');
        } catch (e) {
          /* ignore */
        }
      });
    }
  }

  /* ---------------- Referee page (/pages/invited) — name + trial ATC ---------------- */

  function parseRefereeConfig(root) {
    var el = qs(root, '[data-m-referee-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function parseCartResponse(response) {
    return response.text().then(function (text) {
      var data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = {};
        }
      }
      if (!response.ok) {
        var message =
          (data && (data.description || data.message)) ||
          'Cart request failed (' + response.status + ')';
        throw new Error(message);
      }
      return data;
    });
  }

  function openCartDrawer(cartUrl) {
    if (window.MonthleesCartDrawer && typeof window.MonthleesCartDrawer.refresh === 'function') {
      return window.MonthleesCartDrawer.refresh({ force: true }).then(function () {
        if (typeof window.MonthleesCartDrawer.show === 'function') {
          window.MonthleesCartDrawer.show();
        } else if (typeof window.MonthleesCartDrawer.open === 'function') {
          window.MonthleesCartDrawer.open({ skipFetch: true });
        }
      });
    }
    window.location.assign(cartUrl || '/cart');
    return Promise.resolve();
  }

  function replaceMembershipLines(membershipProductIds) {
    var idSet = {};
    (membershipProductIds || []).forEach(function (id) {
      var n = parseInt(id, 10);
      if (n) idSet[n] = true;
    });
    if (!Object.keys(idSet).length) return Promise.resolve();

    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        var updates = {};
        var has = false;
        (cart.items || []).forEach(function (line) {
          if (idSet[line.product_id]) {
            updates[line.key] = 0;
            has = true;
          }
        });
        if (!has) return null;
        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates }),
        }).then(parseCartResponse);
      });
  }

  function initReferee() {
    var root = document.querySelector('[data-m-referee]');
    if (!root || root.__monthleesRefereeInit) return;
    root.__monthleesRefereeInit = true;

    var config = parseRefereeConfig(root) || {};
    var planButtons = qsa(root, '[data-m-referee-plan]');
    var cta = qs(root, '[data-m-referee-cta]');
    var errorEl = qs(root, '[data-m-referee-cta-error]');
    var selectedPlan = config.defaultPlan || 'basic-annual';

    planButtons.forEach(function (btn) {
      if (btn.classList.contains('is-active') && !btn.disabled) {
        selectedPlan = btn.getAttribute('data-plan') || selectedPlan;
      }
    });

    function showError(message) {
      if (!errorEl) return;
      if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = message;
    }

    function setLoading(loading) {
      if (!cta) return;
      cta.classList.toggle('is-loading', loading);
      cta.disabled = !!loading;
    }

    planButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        selectedPlan = btn.getAttribute('data-plan') || selectedPlan;
        planButtons.forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        showError('');
      });
    });

    if (cta && cta.tagName === 'BUTTON') {
      cta.addEventListener('click', function () {
        showError('');
        if (!config.atcReady) {
          showError(config.errorMissingPlan || 'This trial plan is not configured yet.');
          return;
        }
        var plan = (config.plans && config.plans[selectedPlan]) || null;
        var variantId = plan && parseInt(plan.variantId, 10);
        var sellingPlanId = plan && parseInt(plan.sellingPlanId, 10);
        if (!plan || !plan.ready || !variantId || !sellingPlanId) {
          showError(config.errorMissingPlan || 'This trial plan is not configured yet.');
          return;
        }

        setLoading(true);
        replaceMembershipLines(config.membershipProductIds)
          .then(function () {
            return fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [
                  {
                    id: variantId,
                    quantity: 1,
                    selling_plan: sellingPlanId,
                  },
                ],
              }),
            }).then(parseCartResponse);
          })
          .then(function () {
            try {
              document.body.setAttribute('data-monthlees-membership-in-cart', 'true');
              document.body.setAttribute('data-monthlees-member-pricing-active', 'true');
              document.dispatchEvent(new CustomEvent('monthlees:membership-in-cart', { detail: { inCart: true } }));
              document.dispatchEvent(new CustomEvent('cart:updated'));
            } catch (e) {
              /* ignore */
            }
            return openCartDrawer(config.cartUrl);
          })
          .catch(function (err) {
            showError((err && err.message) || config.errorCart || 'Could not add the free trial to your cart.');
          })
          .then(function () {
            setLoading(false);
          });
      });
    }

    var code = root.getAttribute('data-m-referee-r') || getReferralCodeFromUrl();
    if (!code) return;

    getReferralWho(code)
      .then(function (data) {
        if (!data || !data.ok || !data.firstName) return;
        var name = String(data.firstName);
        qsa(root, '[data-m-referee-name]').forEach(function (el) {
          el.textContent = name;
        });
        qsa(root, '[data-m-referee-pronoun]').forEach(function (el) {
          el.textContent = 'She';
        });
        qsa(root, '[data-m-referee-pronoun-lower]').forEach(function (el) {
          el.textContent = 'she';
        });
        root.classList.add('is-named');
      })
      .catch(function () {
        /* stays on the unnamed fallback already rendered — never break the page */
      });
  }

  /* ---------------- Refer page (/pages/refer) — member view data ---------------- */

  function renderStats(root, data) {
    var joined = qs(root, '[data-m-stat-joined]');
    var earned = qs(root, '[data-m-stat-earned]');
    var pending = qs(root, '[data-m-stat-pending]');
    if (joined) joined.textContent = String(data.joined != null ? data.joined : 0);
    if (earned) earned.textContent = String(data.earned != null ? data.earned : 0);
    if (pending) pending.textContent = String(data.pending != null ? data.pending : 0);
  }

  function formatRewardUsd(amount) {
    var n = Number(amount);
    if (!isFinite(n)) n = 15.79;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(Math.round(n * 100), '${{amount}}');
    }
    return '$' + n.toFixed(2);
  }

  function renderReward(root, data) {
    // Single reward state for every referrer: store credit from rewardUsd.
    var formatted = formatRewardUsd(data && data.rewardUsd != null ? data.rewardUsd : 15.79);
    qsa(root, '[data-m-refer-reward-amount]').forEach(function (el) {
      el.textContent = formatted;
    });
  }

  function renderLink(root, data) {
    var urlEl = qs(root, '[data-m-refer-url]');
    if (urlEl && data.url) {
      urlEl.textContent = data.url.replace(/^https?:\/\//, '');
    }
    var copyBtn = qs(root, '[data-m-refer-copy]');
    if (copyBtn && data.url) {
      copyBtn.setAttribute('data-url', data.url);
    }
    var shareBtn = qs(root, '[data-m-refer-share]');
    if (shareBtn && data.url) {
      shareBtn.setAttribute('data-url', data.url);
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        shareBtn.hidden = false;
      }
    }
  }

  function renderPending(root, rows) {
    var list = qs(root, '[data-m-refer-pending-list]');
    var wrap = qs(root, '[data-m-refer-pending]');
    if (!list) return;
    list.innerHTML = '';
    if (!rows || !rows.length) {
      if (wrap) wrap.hidden = true;
      return;
    }
    if (wrap) wrap.hidden = false;

    rows.forEach(function (row) {
      var item = document.createElement('div');
      item.className = 'm-refer__pending-row';

      var name = document.createElement('span');
      name.className = 'm-refer__pending-name';
      var when = '';
      try {
        if (row.createdAt) when = ' · ' + new Date(row.createdAt).toLocaleDateString();
      } catch (e) {
        when = '';
      }
      name.textContent = 'Pending referral' + when;
      item.appendChild(name);

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'm-refer__nudge';
      btn.textContent = row.nudgedAt ? 'Nudged' : 'Nudge';
      btn.disabled = !!row.nudgedAt;
      btn.addEventListener('click', function () {
        btn.disabled = true;
        btn.textContent = 'Sending…';
        // The nudge endpoint sends no email address — it validates an orderId the
        // caller already owns. That check is the entire security model.
        fetchJson('/apps/precision/referral?action=nudge', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ orderId: row.orderId }),
        })
          .then(function (result) {
            if (result.data && result.data.ok) {
              btn.textContent = 'Nudged';
            } else {
              btn.disabled = false;
              btn.textContent = 'Nudge';
            }
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = 'Nudge';
          });
      });
      item.appendChild(btn);

      list.appendChild(item);
    });
  }

  function bindCopyAndShare(root) {
    var copyBtn = qs(root, '[data-m-refer-copy]');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var url = copyBtn.getAttribute('data-url');
        if (!url) return;
        var done = function () {
          var original = copyBtn.textContent;
          copyBtn.textContent = 'Copied';
          setTimeout(function () {
            copyBtn.textContent = original === 'Copied' ? 'Copy link' : original;
          }, 1600);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, done);
        } else {
          try {
            var input = document.createElement('input');
            input.value = url;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
          } catch (e) {
            /* ignore */
          }
          done();
        }
      });
    }

    var shareBtn = qs(root, '[data-m-refer-share]');
    if (shareBtn) {
      shareBtn.addEventListener('click', function () {
        var url = shareBtn.getAttribute('data-url');
        if (!url || typeof navigator.share !== 'function') return;
        navigator.share({ url: url, title: 'Monthlees' }).catch(function () {
          /* user cancelled — no error state needed */
        });
      });
    }
  }

  function initReferPage() {
    var root = document.querySelector('[data-m-refer]');
    if (!root) return;

    bindCopyAndShare(root);

    if (root.getAttribute('data-m-refer-state') !== 'member') return;

    var countsWrap = qs(root, '[data-m-refer-counts]');
    var errorEl = qs(root, '[data-m-refer-error]');

    fetchJson('/apps/precision/referral', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (result) {
        if (!result.ok || !result.data || !result.data.ok) {
          throw new Error('referral fetch failed');
        }
        var data = result.data;
        renderStats(root, data);
        renderReward(root, data);
        renderLink(root, data);
        renderPending(root, data.pendingReferrals || []);
      })
      .catch(function () {
        // Proxy failing must not error the page — hide the counts, keep the link
        // the section already rendered from Liquid, and say so quietly.
        if (countsWrap) countsWrap.hidden = true;
        if (errorEl) {
          errorEl.hidden = false;
          errorEl.textContent = 'Your referral counts could not load right now — your link above still works.';
        }
      });
  }

  function initAll() {
    initBanner();
    initReferee();
    initReferPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.MonthleesRefer = {
    init: initAll,
  };
})();
